//dashboard/src/components/operationsAssociate/AssetsTable.jsx

import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { FaEdit, FaEye } from "react-icons/fa"; // Imported FaEye instead of FaTrashAlt
import { toast } from 'react-toastify';
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_URL = "https://ims.holdthechild.net";

export default function AssetsTable({ assets, onEdit, onView, onAssetUpdate }) { // Added onView prop
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // Export dropdown state
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Filter + Sort assets by newest first (createdAt DESC)
    const filteredAssets = useMemo(() => {
        const q = search.trim().toLowerCase();

        // clone so we don't mutate original array
        let data = [...assets];

        // Sort by createdAt descending (newest first)
        data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        if (!q) return data;

        return data.filter((a) => {
            // Safely get assignedTo and custodian names
            const assignedToName = a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}` : '';
            const custodianName = a.custodian ? `${a.custodian.firstName} ${a.custodian.lastName}` : '';

            // Create a single searchable string from relevant fields
            const searchableString = `${a.name || ''} ${a.category || ''} ${a.status || ''} ${a.location || ''} ${a.serialNumber || ''} ${assignedToName} ${custodianName}`.toLowerCase();

            return searchableString.includes(q);
        });
    }, [assets, search]);

    // Reset to page 1 if search or data changes
    useEffect(() => {
        setCurrentPage(1);
    }, [search, assets]);

    // Paginated data
    const paginatedAssets = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredAssets.slice(start, start + itemsPerPage);
    }, [filteredAssets, currentPage]);

    const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

    // Map for export
    const mapAssetToExportRow = (a) => ({
        Name: a.name || "",
        Category: a.category || "",
        "Serial Number": a.serialNumber || "",
        Status: a.status || "",
        Location: a.location || "",
        "Assigned To": a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}` : "Unassigned",
        "Custodian": a.custodian ? `${a.custodian.firstName} ${a.custodian.lastName}` : "Unassigned",
        "Created At": moment(a.createdAt).format("YYYY-MM-DD"),
    });

    // Export Excel
    const exportToExcel = () => {
        if (!filteredAssets.length) return alert("No assets to export!");
        const exportData = filteredAssets.map(mapAssetToExportRow);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Assets");
        worksheet["!cols"] = Object.keys(exportData[0]).map((col) => ({
            wch: Math.max(
                col.length,
                ...exportData.map((row) => (row[col] ? row[col].toString().length : 0))
            ) + 5,
        }));
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(
            new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            "assets_export.xlsx"
        );
    };

    // Export CSV
    const exportToCSV = () => {
        if (!filteredAssets.length) return alert("No assets to export!");
        const exportData = filteredAssets.map(mapAssetToExportRow);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "assets_export.csv");
    };

    // Export PDF
    const exportToPDF = () => {
        if (!filteredAssets.length) return alert("No assets to export!");
        const exportData = filteredAssets.map(mapAssetToExportRow);
        const doc = new jsPDF("l", "pt", "a4");
        const columns = Object.keys(exportData[0]);
        doc.setFontSize(18);
        doc.setTextColor(237, 125, 49);
        doc.text("Assets Export", 40, 40);
        autoTable(doc, {
            startY: 60,
            head: [columns],
            body: exportData.map((row) => columns.map((col) => row[col])),
            styles: { fontSize: 9, cellPadding: 4 },
            headStyles: { fillColor: [237, 125, 49] },
            theme: "striped",
            margin: { left: 20, right: 20 },
        });
        doc.save("assets_export.pdf");
    };

    const handleExport = (format) => {
        setExportDropdownOpen(false);
        if (format === "excel") exportToExcel();
        else if (format === "csv") exportToCSV();
        else if (format === "pdf") exportToPDF();
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setExportDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleView = (asset) => {
        if (onView) {
            onView(asset);
        }
    };


    const getStatusColor = (status) => {
        switch (status) {
            case "Active": return 'bg-green-100 text-green-800';
            case "In Use": return 'bg-blue-100 text-blue-800';
            case "Needs Review": return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-red-100 text-red-800';
        }
    };

    return (
        <div className="mt-8 bg-white rounded shadow p-4">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Assets Table</h2>

            {/* Search + Export */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <input
                    type="search"
                    placeholder="Search assets by name, category, status..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />

                {/* Export dropdown */}
                <div className="relative inline-block text-left" ref={dropdownRef}>
                    <button
                        onClick={() => setExportDropdownOpen((o) => !o)}
                        className="inline-flex justify-center w-full rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                        Export
                        <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {exportDropdownOpen && (
                        <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                            <div className="py-1">
                                {"excel,csv,pdf".split(",").map((format) => (
                                    <button
                                        key={format}
                                        onClick={() => handleExport(format)}
                                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
                                    >
                                        Export as {format.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="bg-orange-100 text-gray-600 text-left">
                            <th className="px-4 py-2 text-orange-600">Name</th>
                            <th className="px-4 py-2 text-orange-600">Category</th>
                            <th className="px-4 py-2 text-orange-600">Serial Number</th>
                            <th className="px-4 py-2 text-orange-600">Status</th>
                            <th className="px-4 py-2 text-orange-600">Location</th>
                            <th className="px-4 py-2 text-orange-600">Assigned To</th>
                            <th className="px-4 py-2 text-orange-600">Custodian</th>
                            <th className="px-4 py-2 text-orange-600 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedAssets.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="text-center text-gray-500 py-4">
                                    No assets found.
                                </td>
                            </tr>
                        ) : (
                            paginatedAssets.map((asset) => (
                                <tr key={asset._id} className="border-b hover:bg-gray-50">
                                    <td className="px-4 py-2 font-medium text-gray-900">{asset.name}</td>
                                    <td className="px-4 py-2 text-gray-500">{asset.category}</td>
                                    <td className="px-4 py-2 text-gray-500">{asset.serialNumber}</td>
                                    <td className="px-4 py-2">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(asset.status)}`}>
                                            {asset.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-gray-500">{asset.location || "N/A"}</td>
                                    <td className="px-4 py-2 text-gray-500">
                                        {asset.assignedTo ? `${asset.assignedTo.firstName} ${asset.assignedTo.lastName}` : "Unassigned"}
                                    </td>
                                    <td className="px-4 py-2 text-gray-500">
                                        {asset.custodian ? `${asset.custodian.firstName} ${asset.custodian.lastName}` : "Unassigned"}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        <button onClick={() => handleView(asset)} className="text-orange-600 hover:text-orange-900 mr-3" title="View Details">
                                            <FaEye />
                                        </button>
                                        <button onClick={() => onEdit(asset)} className="text-orange-600 hover:text-orange-900 mr-3" title="Edit Asset">
                                            <FaEdit />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                    <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`rounded px-3 py-1 ${currentPage === 1 ? "cursor-not-allowed bg-gray-300" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
                    >
                        Prev
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`rounded px-3 py-1 ${currentPage === i + 1 ? "bg-orange-300 text-orange-900 font-semibold" : "bg-gray-200 hover:bg-gray-300"}`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`rounded px-3 py-1 ${currentPage === totalPages ? "cursor-not-allowed bg-gray-300" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}